import { Box, Button, CircularProgress, Typography } from '@mui/material';

// =============================================================================
// Types
// =============================================================================

interface ImportProgressStepProps {
  message: string;
  onCancel?: () => void;
  isCancelling?: boolean;
  /** 0–100. When provided, shows a determinate progress indicator. */
  progressPercentage?: number;
  /** e.g. "12 of 234 records" */
  progressLabel?: string;
}

// =============================================================================
// Component
// =============================================================================

export const ImportProgressStep = ({
  message,
  onCancel,
  isCancelling = false,
  progressPercentage,
  progressLabel,
}: ImportProgressStepProps) => {
  const isDeterminate = progressPercentage != null && progressPercentage > 0 && progressPercentage < 100;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        gap: 3,
        px: 2,
      }}
    >
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          size={48}
          variant={isDeterminate ? 'determinate' : 'indeterminate'}
          value={isDeterminate ? progressPercentage : undefined}
        />
        {isDeterminate && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {Math.round(progressPercentage)}%
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {message}
        </Typography>
        {progressLabel && (
          <Typography variant="caption" color="text.secondary">
            {progressLabel}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
          You can leave this page and come back anytime.
        </Typography>
      </Box>

      {onCancel && (
        <Button
          variant="text"
          size="small"
          onClick={onCancel}
          disabled={isCancelling}
        >
          {isCancelling ? 'Cancelling...' : 'Cancel Import'}
        </Button>
      )}
    </Box>
  );
};
